import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Edit, AssignmentInd, AddCircleOutline, FileDownloadOutlined, GridOnOutlined } from '@mui/icons-material';
import api from '../../services/api';
import { EmptyState, LoadingState, PageHeader, PageLayout, SectionCard } from '../../components/ui';

const AssetListPage = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    assetType: '',
    user: '',
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const params = {
        status: filters.status,
        asset_type: filters.assetType,
        user_employee_code: filters.user,
      };
      const response = await api.get('/assets', { params });
      setAssets(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching assets:', error);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      assetType: '',
      user: '',
    });
  };

  // Download an Excel file from an authenticated endpoint (blob -> save).
  const downloadFile = async (url, filename, params = {}) => {
    try {
      const res = await api.get(url, { responseType: 'blob', params });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'assigned':
        return 'success';
      case 'returned':
        return 'secondary';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <PageLayout backgroundAccent>
      <PageHeader
        overline="Inventory"
        title="Asset Management"
        subtitle="Track assignment status, employee ownership, and asset condition across the organization."
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<GridOnOutlined />}
              onClick={() => downloadFile('/assets/export/template', 'assets-template.xlsx')}
            >
              Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlined />}
              onClick={() => downloadFile('/assets/export', `assets-${new Date().toISOString().slice(0, 10)}.xlsx`, {
                status: filters.status,
                asset_type: filters.assetType,
                user_employee_code: filters.user,
              })}
            >
              Export Excel
            </Button>
            <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => navigate('/assets/new')}>
              Add Asset
            </Button>
          </Stack>
        }
      />

      <SectionCard title="Filters" sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField select label="Status" name="status" value={filters.status} onChange={handleFilterChange} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="assigned">Assigned</MenuItem>
            <MenuItem value="returned">Returned</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
          </TextField>

          <TextField
            select
            label="Asset Type"
            name="assetType"
            value={filters.assetType}
            onChange={handleFilterChange}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="laptop">Laptop</MenuItem>
            <MenuItem value="phone">Phone</MenuItem>
            <MenuItem value="vehicle">Vehicle</MenuItem>
            <MenuItem value="equipment">Equipment</MenuItem>
          </TextField>

          <TextField
            label="Employee Code"
            name="user"
            placeholder="Enter employee code"
            value={filters.user}
            onChange={handleFilterChange}
            sx={{ minWidth: 180 }}
          />

          <Box sx={{ ml: { md: 'auto' } }}>
            <Button variant="outlined" color="inherit" onClick={handleClearFilters}>
              Clear
            </Button>
          </Box>
        </Stack>
      </SectionCard>

      <SectionCard title={`Assets (${assets.length})`}>
        <TableContainer component={Paper} elevation={0}>
          {loading ? (
            <LoadingState rows={8} header={false} />
          ) : assets.length === 0 ? (
            <EmptyState
              compact
              title="No assets found"
              description="Try adjusting the filters or add a new asset."
              actionLabel="Add Asset"
              onAction={() => navigate('/assets/new')}
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Asset Tag</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Assignment Date</TableCell>
                  <TableCell>Condition</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.map((asset) => (
                  <TableRow hover key={asset.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{asset.asset_tag}</TableCell>
                    <TableCell>{asset.asset_type}</TableCell>
                    <TableCell>
                      {asset.user ? `${asset.user.name} (${asset.user.employee_code})` : 'Unassigned'}
                    </TableCell>
                    <TableCell>
                      {asset.assignment_date ? new Date(asset.assignment_date).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>{asset.condition || '-'}</TableCell>
                    <TableCell>
                      <Chip label={asset.status} color={getStatusColor(asset.status)} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => navigate(`/assets/${asset.id}`)}>
                        <Edit />
                      </IconButton>

                      {asset.user ? (
                        <IconButton color="secondary" onClick={() => navigate(`/admin/users/${asset.user.id}`)}>
                          <AssignmentInd />
                        </IconButton>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : null}
      </SectionCard>
    </PageLayout>
  );
};

export default AssetListPage;

